import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { colors } from "@zapalacorp/tokens";
import { Add, Trash } from "iconsax-react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

import { Input } from "../../../../../../../../components/Form";
import Button, { Color } from "../../../../../../../../components/Form/Button";
import CollapsablePanel from "../../../../../../../../components/Generic/Collapse";
import Modal from "../../../../../../../../components/Generic/Modal";
import { NotificationToast } from "../../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../../context/StateContext";
import { useChecklistSealsAction } from "../../../../../../../../hooks/useChecklistSealsAction";
import useYupValidation from "../../../../../../../../hooks/useYupValidation";
import { ChecklistSeal, ChecklistSealPicture } from "../../../../../../../../models/checklist.seal";
import { JobType } from "../../../../../../../../models/jobType";
import { Order } from "../../../../../../../../models/order";
import { StorageBox } from "../../../../../../../../models/storage.box";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { StorageBoxTypeSealRule } from "../../../../../../../../models/storage.box.type.seal.rule";
import { convertEventToFile } from "../../../../../../../../util/convertEventToFile";
import { base, BASE64_REGEX } from "../../../../../../../../util/convertToBase64";
import { SealPictureBelongsGroup } from "../../../../../../../../util/enums";
import { ChecklistSealsKeys } from "../../../../../../../../util/queries";
import Picture from "./components/Picture";
import { Skeleton } from "./components/Skeleton";
import useStyles, {
  AddNewSealWrapper,
  CollapsablePanelTitle,
  CounterBadge,
  EmptyStateImageWrapper,
  EmptyStateInfoWrapper,
  EmptyStateSubTitleInfo,
  EmptyStateTitleInfo,
  EmptyStateWrapper,
  FieldHeader,
  FieldLabel,
  FileInput,
  ImageComponent,
  RemoveFieldWrapper,
  Title,
} from "./styles";

export interface IHandleSealRulesChecklist {
  sealRules?: StorageBoxTypeSealRule;
}

export interface SealRulesChecklistModalHandle {
  close: () => void;
  open: ({ sealRules }: IHandleSealRulesChecklist) => void;
}

interface ISealRulesChecklistModal {
  job?: Order;
  isLoadingChecklistSeals: boolean;
  handleRemoveStorageBox: () => void;
  jobType?: JobType;
  storageBox?: StorageBox;
  storageBoxType?: StorageBoxType;
  checklistSeals?: ChecklistSeal;
  setStorageBoxesChecklistsSeals: React.Dispatch<React.SetStateAction<ChecklistSeal[] | undefined>>;
}

interface ICollapsablePanelTitle {
  title: string;
  count: number;
}

interface IHandleRemoveSealField {
  renderDeleteIcon?: boolean;
  handleRemoveSeal: () => void;
}

interface IHandleFileRead {
  belongsGroup: SealPictureBelongsGroup;
  event: React.ChangeEvent<HTMLInputElement>;
}

const generateRandomPrefix = () => {
  const randomNum = Math.floor(Math.random() * 1000000000);

  return randomNum.toString();
};

const SealRulesChecklistModal: ForwardRefRenderFunction<
  SealRulesChecklistModalHandle,
  ISealRulesChecklistModal
> = (props, ref) => {
  const {
    job,
    handleRemoveStorageBox,
    isLoadingChecklistSeals,
    jobType,
    storageBox,
    storageBoxType,
    checklistSeals,
    setStorageBoxesChecklistsSeals,
  } = props;

  const classes = useStyles();
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();
  const queryClient = useQueryClient();

  const { createCheckedChecklistSeals, isCheckListSealsLoading } = useChecklistSealsAction();

  const [open, setOpen] = useState<boolean>(false);

  const [checklistSeal, setChecklistSeal] = useState<ChecklistSeal>();
  const [inventorySealRules, setInventorySealRules] = useState<StorageBoxTypeSealRule>();
  const inputRefSpecialItems = useRef<HTMLInputElement>(null);
  const inputRefCaulked = useRef<HTMLInputElement>(null);
  const inputRefPackedAndSealed = useRef<HTMLInputElement>(null);

  const [errors, setErrors] = useState<yup.ValidationError[]>([]);

  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const filterPicturesByGroup = useCallback(
    (groupType: SealPictureBelongsGroup, data?: ChecklistSeal["pictures"]) => {
      if (data) {
        const filteredData = data.filter((content) => {
          return content.belongsGroup === groupType;
        });

        return filteredData;
      }
    },
    [checklistSeal?.pictures]
  );

  const specialItemsPictures = filterPicturesByGroup(
    SealPictureBelongsGroup.SPECIAL_ITEMS,
    checklistSeal?.pictures
  );

  const specialItemsPicturesCount = specialItemsPictures?.length;

  const caulkedItemsPictures = filterPicturesByGroup(
    SealPictureBelongsGroup.CAULKED,
    checklistSeal?.pictures
  );

  const caulkedItemsPicturesCount = caulkedItemsPictures?.length;

  const packedAndSealedItemsPictures = filterPicturesByGroup(
    SealPictureBelongsGroup.PACKED_AND_SEALED,
    checklistSeal?.pictures
  );

  const packedAndSealedItemsPicturesCount = packedAndSealedItemsPictures?.length;

  const getEmptySealRule = () => {
    return {
      jobType: job?.jobType,
      companyId: currentUser?.companyId,
      jobTypeId: jobType?.id,
      sealsQuantity: 1,
    } as StorageBoxTypeSealRule;
  };

  useEffect(() => {
    setChecklistSeal((prev) => {
      return {
        ...prev,
        storageBoxId: storageBox?.id,
        storageBoxTypeSealRuleId: inventorySealRules?.id,
        jobId: job?.id,
        companyId: currentUser?.companyId,
      };
    });

    if (checklistSeals) {
      setChecklistSeal((prev) => {
        return { ...prev, seals: checklistSeals?.seals, pictures: checklistSeals?.pictures };
      });
    }

    if (!checklistSeals && inventorySealRules?.sealsQuantity) {
      const newSeals = Array.from({ length: inventorySealRules?.sealsQuantity }, (_, index) => ({
        sequence: index + 1,
        label: `${translate("sealNumber")} #${index + 1}`,
        number: "",
      }));

      setChecklistSeal((prev) => {
        return { ...prev, seals: newSeals };
      });
    }

    if (!checklistSeals && !inventorySealRules) {
      setInventorySealRules(getEmptySealRule());
    }
  }, [checklistSeals, inventorySealRules]);

  const getNewEmptySeal = () => {
    const getSealRulesLength = checklistSeal?.seals?.length ?? 1;

    return {
      sequence: getSealRulesLength + 1,
      number: "",
    };
  };

  const handleNewSeal = () => {
    const newSeal = getNewEmptySeal();

    setChecklistSeal((prev) => {
      const prevSeals = prev?.seals;

      prevSeals?.push(newSeal);

      return { ...prev, seals: prevSeals };
    });
  };

  useImperativeHandle(ref, () => ({ open: onOpenModal, close: onCloseModal }));

  const onCloseModal = () => {
    setChecklistSeal(undefined);
    setInventorySealRules(undefined);
    setOpen(false);
    setErrors([]);
    handleRemoveStorageBox();
  };

  const onOpenModal = ({ sealRules }: IHandleSealRulesChecklist) => {
    setInventorySealRules(sealRules);
    setOpen(true);
  };

  const isValid = () => {
    try {
      schema.validateSync(checklistSeal, { abortEarly: false });
      setErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error?.inner);
      }

      return false;
    }
  };

  const schema = yup.object().shape({
    seals: yup
      .array()
      .of(
        yup.object().shape({
          status: yup.string().nullable(),
          number: yup
            .string()
            .required(translate("thisFieldIsRequired"))
            .typeError(translate("thisFieldIsRequired")),
          sequence: yup
            .number()
            .min(1, translate("sealsValueMustBeGreaterThan0"))
            .required(translate("sealsValueMustBeGreaterThan0"))
            .typeError(translate("sealsValueMustBeGreaterThan0")),
        })
      )
      .nullable(),
  });

  const handleSubmit = () => {
    if (isValid() && checklistSeal)
      createCheckedChecklistSeals(checklistSeal, async (data) => {
        setStorageBoxesChecklistsSeals((prev) => {
          const prevChecklistSeals = prev ?? [];

          prevChecklistSeals.push(data);

          return prevChecklistSeals;
        });
        onCloseModal();
        await queryClient.invalidateQueries({ queryKey: [ChecklistSealsKeys.getAll([job?.id])] });
      });
  };

  const renderFieldLabel = (index: number) => {
    return (
      <FieldHeader>
        <FieldLabel>{`${translate("sealNumber")} #${index + 1}`}</FieldLabel>
      </FieldHeader>
    );
  };

  const renderDeleteField = ({ renderDeleteIcon, handleRemoveSeal }: IHandleRemoveSealField) => {
    if (renderDeleteIcon)
      return (
        <RemoveFieldWrapper
          onClick={(e) => {
            e.stopPropagation();
            if (!isCheckListSealsLoading) {
              handleRemoveSeal();
            }
          }}
        >
          <Trash size="16" color={isCheckListSealsLoading ? colors.gray500 : colors.error500} />
        </RemoveFieldWrapper>
      );

    return null;
  };

  const renderCollapsablePanelTitle = ({ title, count }: ICollapsablePanelTitle) => {
    return (
      <CollapsablePanelTitle>
        {title}
        <CounterBadge>{count}</CounterBadge>
      </CollapsablePanelTitle>
    );
  };

  const handleFileRead = async ({ belongsGroup, event }: IHandleFileRead) => {
    const file = await convertEventToFile(event, translate, ["jpg", "png", "jpeg"]);
    const randomNamePrefix = generateRandomPrefix();

    if (file && file?.uri) {
      const base64Splitted = file?.uri?.split(";base64,");

      if (base64Splitted && base64Splitted?.length > 0 && base64Splitted[1]) {
        const sealsPicturesToCreate = {
          name: `${randomNamePrefix}-${file.name}`,
          mediaLink: base64Splitted[1],
          belongsGroup,
        } as ChecklistSealPicture;

        setChecklistSeal((prev) => {
          const prevPictures =
            prev?.pictures === undefined ? ([] as ChecklistSealPicture[]) : prev?.pictures;

          prevPictures?.push(sealsPicturesToCreate);

          return { ...prev, pictures: prevPictures };
        });
        NotificationToast.success(translate("photoAddedSuccessfully"), {
          id: "seals.checklist.photo.add",
        });
      }
    }
    event.target.value = "";
  };

  const handleRemovePicture = (picture: ChecklistSealPicture) => {
    const newChecklistPictures = checklistSeal?.pictures?.filter(
      (image) => image.name !== picture.name
    );

    setChecklistSeal((prev) => {
      return { ...prev, pictures: newChecklistPictures };
    });
  };

  const renderEmptyImagesState = (
    belongsGroup: SealPictureBelongsGroup,
    reference: React.RefObject<HTMLInputElement>
  ) => {
    return (
      <EmptyStateWrapper>
        <EmptyStateImageWrapper>
          <ImageComponent src="https://shipeezi-images-dev.s3.ap-southeast-2.amazonaws.com/image_empty_state.png" />
        </EmptyStateImageWrapper>

        <EmptyStateInfoWrapper>
          <EmptyStateTitleInfo>{translate("itLooksLikeThereIsNoPhotosYet")}</EmptyStateTitleInfo>
          <EmptyStateSubTitleInfo>
            {translate("youCanStartByAddingANewPhotoOnTheButtonBelow")}
          </EmptyStateSubTitleInfo>
        </EmptyStateInfoWrapper>

        <Button
          customColor={Color.SECONDARY_DARK}
          startIcon={<Add size="20" color={colors.white} />}
          className={classes.button}
          onClick={() => reference?.current?.click()}
          disabled={isCheckListSealsLoading || !!checklistSeals}
        >
          {translate("addAPhoto")}
        </Button>
        <FileInput
          type="file"
          accept=".jpeg,.png,.jpg"
          ref={reference}
          onChange={(event) => handleFileRead({ belongsGroup, event })}
          disabled={isCheckListSealsLoading || !!checklistSeals}
        />
      </EmptyStateWrapper>
    );
  };

  const renderChecklistPictures = (
    belongsGroup: SealPictureBelongsGroup,
    reference: React.RefObject<HTMLInputElement>,
    pictures?: ChecklistSealPicture[]
  ) => {
    if (pictures && pictures?.length > 0) {
      return (
        <Grid container alignItems="center" spacing={2}>
          {pictures.map((picture) => {
            if (picture && picture?.mediaLink)
              return (
                <Grid key={picture?.mediaLink} item xs={4} justifyContent="flex-end">
                  <Picture
                    src={
                      BASE64_REGEX.test(picture?.mediaLink)
                        ? `${base()}${picture?.mediaLink}`
                        : picture?.mediaLink
                    }
                    skeleton={{ className: classes.skeleton }}
                    className={classes.image}
                    onRemove={() => handleRemovePicture(picture)}
                    disabled={isCheckListSealsLoading || !!checklistSeals}
                    // onImageClick={() => onOpenImgModal(index)}
                  />
                </Grid>
              );

            return <></>;
          })}

          <Grid item xs={12}>
            <Button
              variant="text"
              customColor={Color.DEFAULT}
              onClick={() => reference?.current?.click()}
              startIcon={<Add size="20px" color={colors.brandBlue500} />}
              disabled={isCheckListSealsLoading || !!checklistSeals}
            >
              {translate("addAnotherPhoto")}
            </Button>
            <FileInput
              type="file"
              accept=".jpeg,.png,.jpg"
              ref={reference}
              onChange={(event) => handleFileRead({ belongsGroup, event })}
              disabled={isCheckListSealsLoading || !!checklistSeals}
            />
          </Grid>
        </Grid>
      );
    }

    return renderEmptyImagesState(belongsGroup, reference);
  };

  const handleRemoveSeal = (sequence: number) => {
    const newOrderSeals = checklistSeal?.seals?.filter((item) => item.sequence !== sequence);

    const fixedSeals = newOrderSeals?.sort((a, b) => a.sequence - b.sequence);
    let sequenceNumber = 1;

    if (fixedSeals) {
      for (const seal of fixedSeals) {
        seal.sequence = sequenceNumber;
        sequenceNumber += 1;
      }
    }

    setChecklistSeal((prev) => {
      return { ...prev, seals: fixedSeals };
    });
  };

  const handleSealValue = (sequence: number, numberValue: string) => {
    setChecklistSeal((prev) => {
      return {
        ...prev,
        seals: prev?.seals?.map?.((seal) => {
          return seal.sequence === sequence ? { ...seal, number: numberValue } : seal;
        }),
      };
    });
  };

  const isFieldRequired = (fieldIndex: number): boolean => {
    const sealsQuantity = checklistSeals?.seals?.length
      ? checklistSeals?.seals?.length
      : inventorySealRules?.sealsQuantity ?? 1;

    return fieldIndex < sealsQuantity;
  };

  return (
    <Modal
      open={open}
      title={translate("checklist")}
      handleClose={onCloseModal}
      onClose={onCloseModal}
      showActionButtons
      actionButtonProps={{
        customColorSave: Color.SUCCESS,
        onCancel: () => onCloseModal(),
        onSave: () => {
          handleSubmit();
        },
        textSave: translate("save"),
        loading: isCheckListSealsLoading,
        saveDisabled: isCheckListSealsLoading || !!checklistSeals,
      }}
      className={classes.modalStyles}
    >
      <Grid container direction="row" spacing={3}>
        <Grid item xs={12}>
          <Title>{translate("sealNumbers")}</Title>
        </Grid>
        {isLoadingChecklistSeals ? (
          <Skeleton />
        ) : (
          <>
            <Grid item xs={12}>
              <Grid item xs={12} container spacing={2}>
                {checklistSeal?.seals &&
                  checklistSeal?.seals?.map((sealRule, index) => {
                    const lastSealRules = checklistSeal?.seals && checklistSeal.seals.length - 1;

                    return (
                      <Grid item xs={4} key={`seal-number-${index}`}>
                        <Grid item xs container spacing={2}>
                          <Grid item xs>
                            <Input
                              label={renderFieldLabel(index)}
                              type="text"
                              placeholder={translate("typeHere")}
                              fullWidth
                              required
                              value={sealRule.number ?? ""}
                              labelClassName={classes.label}
                              renderIconInsideLabel={() =>
                                renderDeleteField({
                                  renderDeleteIcon: !isFieldRequired(index),
                                  handleRemoveSeal: () => handleRemoveSeal(sealRule.sequence),
                                })
                              }
                              onChange={(e) => {
                                handleSealValue(sealRule.sequence, e.target.value);
                              }}
                              disabled={isCheckListSealsLoading || !!checklistSeals}
                              error={fieldHasError(`seals[${index}].number`)}
                              errorMessage={getErrorByField(`seals[${index}].number`)}
                            />
                          </Grid>
                          {index === lastSealRules && !checklistSeals && !isCheckListSealsLoading && (
                            <AddNewSealWrapper
                              onClick={isCheckListSealsLoading ? () => {} : handleNewSeal}
                            >
                              <Add size="24" color={colors.black} />
                            </AddNewSealWrapper>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <CollapsablePanel
                title={renderCollapsablePanelTitle({
                  title: `${translate("photosOfSpecialItemsInside")} '${storageBoxType?.name}'`,
                  count: specialItemsPicturesCount ?? 0,
                })}
                panelClassName={classes.panel}
                panelContentClassName={classes.panelContent}
              >
                {renderChecklistPictures(
                  SealPictureBelongsGroup.SPECIAL_ITEMS,
                  inputRefSpecialItems,
                  specialItemsPictures
                )}
              </CollapsablePanel>
            </Grid>
            <Grid item xs={12}>
              <CollapsablePanel
                title={renderCollapsablePanelTitle({
                  title: `${translate("photosOfCaulked")} '${storageBoxType?.name}'`,
                  count: caulkedItemsPicturesCount ?? 0,
                })}
                panelClassName={classes.panel}
                panelContentClassName={classes.panelContent}
              >
                {renderChecklistPictures(
                  SealPictureBelongsGroup.CAULKED,
                  inputRefCaulked,
                  caulkedItemsPictures
                )}
              </CollapsablePanel>
            </Grid>
            <Grid item xs={12}>
              <CollapsablePanel
                title={renderCollapsablePanelTitle({
                  title: `${translate("photosOfPackedAndSealed")} '${storageBoxType?.name}'`,
                  count: packedAndSealedItemsPicturesCount ?? 0,
                })}
                panelClassName={classes.panel}
                panelContentClassName={classes.panelContent}
              >
                {renderChecklistPictures(
                  SealPictureBelongsGroup.PACKED_AND_SEALED,
                  inputRefPackedAndSealed,
                  packedAndSealedItemsPictures
                )}
              </CollapsablePanel>
            </Grid>
          </>
        )}
      </Grid>
    </Modal>
  );
};

export default forwardRef(SealRulesChecklistModal);
